import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { VoraLogo } from "@/components/brand/VoraLogo";

/**
 * Production Privacy Policy (v1.0).
 *
 * Routing, layout, and styling are unchanged — body content only.
 * "[Insert Email]" remains a literal placeholder until the contact
 * address is finalized.
 */
export default function Privacy() {
  return (
    <div className="vora-legal-page" data-testid="privacy-page">
      <header className="vora-legal-header">
        <Link to="/" className="flex items-center gap-2" data-testid="privacy-logo-link">
          <VoraLogo variant="icon" size={22} />
          <span className="text-base font-bold font-heading tracking-tight" style={{ color: "#111111" }}>VORA</span>
        </Link>
        <Link
          to="/signup"
          className="text-sm font-medium flex items-center gap-1.5"
          style={{ color: "#5F6368" }}
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </header>

      <main className="vora-legal-main">
        <div className="vora-legal-card">
          <h1 className="vora-legal-title" data-testid="privacy-title">Privacy Policy</h1>
          <p className="vora-legal-meta" data-testid="privacy-updated">
            Last Updated: April 30, 2026 · Version v1.0
          </p>

          <section className="vora-legal-section">
            <h2>1. Overview</h2>
            <p>This Privacy Policy explains how VORA collects, uses, and protects your information.</p>
            <p>By using the Platform, you consent to the practices described in this Policy.</p>
          </section>

          <section className="vora-legal-section">
            <h2>2. Information We Collect</h2>
            <p>We collect the following types of information:</p>

            <p className="vora-legal-subhead">Account Information:</p>
            <ul>
              <li>Name</li>
              <li>Email address</li>
            </ul>

            <p className="vora-legal-subhead">Profile Information:</p>
            <ul>
              <li>Age</li>
              <li>Height</li>
              <li>Weight</li>
              <li>Gender</li>
              <li>Goals</li>
            </ul>

            <p className="vora-legal-subhead">Usage Data:</p>
            <ul>
              <li>Interactions with the Platform</li>
              <li>Features used</li>
              <li>Device and log information</li>
            </ul>
          </section>

          <section className="vora-legal-section">
            <h2>3. Purpose of Collection</h2>
            <p>We collect information to:</p>
            <ul>
              <li>Provide and operate the Platform</li>
              <li>Generate personalized nutrition outputs</li>
              <li>Improve AI systems and features</li>
              <li>Maintain security and prevent misuse</li>
            </ul>
          </section>

          <section className="vora-legal-section">
            <h2>4. AI Processing</h2>
            <p>Your data may be processed by automated systems to generate outputs.</p>
            <p>These systems:</p>
            <ul>
              <li>Do not guarantee accuracy</li>
              <li>Operate based on available inputs</li>
              <li>May evolve over time</li>
            </ul>
          </section>

          <section className="vora-legal-section">
            <h2>5. Data Sharing</h2>
            <p>We may share information with:</p>
            <ul>
              <li>Service providers (hosting, analytics, infrastructure)</li>
              <li>Integrated partners (e.g., grocery or delivery services)</li>
            </ul>
            <p>We do not sell your personal information.</p>
          </section>

          <section className="vora-legal-section">
            <h2>6. Data Retention</h2>
            <p>We retain information as long as necessary to:</p>
            <ul>
              <li>Provide services</li>
              <li>Meet legal obligations</li>
            </ul>
          </section>

          <section className="vora-legal-section">
            <h2>7. Security</h2>
            <p>We use reasonable safeguards to protect your information.</p>
            <p>However, no system is completely secure.</p>
          </section>

          <section className="vora-legal-section">
            <h2>8. Your Rights (Canada)</h2>
            <p>You may:</p>
            <ul>
              <li>Request access to your data</li>
              <li>Request corrections</li>
              <li>Request deletion (subject to legal limitations)</li>
            </ul>
          </section>

          <section className="vora-legal-section">
            <h2>9. International Use</h2>
            <p>If the Platform expands globally, your data may be processed in other jurisdictions.</p>
          </section>

          <section className="vora-legal-section">
            <h2>10. Age Restriction</h2>
            <p>The Platform is not intended for individuals under 18.</p>
          </section>

          <section className="vora-legal-section">
            <h2>11. Changes to this Policy</h2>
            <p>We may update this Privacy Policy from time to time.</p>
          </section>

          <section className="vora-legal-section">
            <h2>12. Contact</h2>
            <p>For privacy inquiries: [Insert Email]</p>
          </section>

          <div className="vora-legal-footer">
            <Link to="/signup" className="vora-legal-back-btn" data-testid="privacy-footer-signup">
              Back to sign-up
            </Link>
            <Link to="/login" className="vora-legal-back-btn vora-legal-back-btn--ghost" data-testid="privacy-footer-login">
              Back to log-in
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
